'use client';

import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as MediaAssetAPI from '@/api/media-asset';
import { DropZone, BlockStack, Text } from '@shopify/polaris';
import { CompletedStandaloneMediaAsset, MediaAssetType } from '@/types/media';
import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import { MediaAssetUploadProgressCard } from '@/components/media-asset/MediaAssetUploadProgressCard';
import { UserUploadedFile } from '@/api/upload-temp-file';
import { useCurrentApp } from '@/stores/userDataStore';
import MediaGrid from '@/components/media-asset/MediaGrid';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import { useUploadMediaAsset } from '@/features/media-modal/hooks/useUploadMediaAsset';
import { getOutputBucketUrl } from '@/utils/public-urls';
import { SUPPORTED_IMAGE_EXTENSIONS, SUPPORTED_FILE_EXTENSIONS } from '@/constants/media';
import { getDisplayableFileExtensionList } from '@/utils/media';
import { getListOfItemsAsString } from '@tolstoy/utils/stringUtils';
export type UploadMediaAssetModalSectionProps = ImagesModalSectionProps & {
  isOnlyImages?: boolean;
};

type UploadFile = {
  id?: string;
  uploadedTempFile?: UserUploadedFile;
  progress: number;
  isUploaded: boolean;
  type: string;
  size: number;
  file: File;
};

export const UploadMediaAssetModalSection = ({
  setSelectedMedia,
  selectedMedia,
  isOnlyImages = false,
  multiSelection = true,
}: UploadMediaAssetModalSectionProps) => {
  const { appKey, isLoggedIn } = useCurrentApp();
  const { files, handleUpload } = useUploadMediaAsset({
    setSelectedMedia,
    multiSelection,
  });

  const { data: uploadedAssets } = useQuery({
    queryKey: ['uploaded-assets', appKey],
    queryFn: () => MediaAssetAPI.getUploadedMediaAssets({ appKey }),
    enabled: !!appKey && isLoggedIn,
  });

  const handleDropZoneDrop = async (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    handleUpload(acceptedFiles);
  };

  const handleSelect = useCallback(
    (media: CompletedStandaloneMediaAsset, selected: boolean) => {
      const formattedMedia = { ...media, src: `${getOutputBucketUrl()}/${media.result}` };

      setSelectedMedia((prev) => {
        if (!multiSelection) {
          return selected ? [formattedMedia] : [];
        }

        return selected ? [...prev, formattedMedia] : prev.filter((item) => item.id !== media.id);
      });
    },
    [setSelectedMedia, multiSelection]
  );

  const mediaItems = useMemo(
    () => [
      ...files.map((file) => ({
        id: file.id!,
        type: file.type.startsWith('video/') ? MediaAssetType.video : MediaAssetType.image,
        uploadProgress: file,
      })),
      ...(uploadedAssets?.mediaAssets || []),
    ],
    [files, uploadedAssets?.mediaAssets]
  );

  const renderItem = useCallback(
    (item) => {
      if ('uploadProgress' in item) {
        return (
          <MediaAssetUploadProgressCard
            key={item.id}
            file={item.uploadProgress.file}
            progress={item.uploadProgress.progress}
            size="small"
            uploadedTempFile={item.uploadProgress.uploadedTempFile}
          />
        );
      }
      return (
        <MediaAssetCard
          key={item.id}
          size="small"
          media={item}
          hideUseAction
          selected={selectedMedia.some((media) => media.id === item.id)}
          onSelect={(media, selected) => handleSelect(media as CompletedStandaloneMediaAsset, selected)}
        />
      );
    },
    [handleSelect, selectedMedia]
  );

  return (
    <BlockStack gap="400">
      <Text variant="headingMd" as="h2">
        Upload Media
      </Text>
      <div className="w-full">
        <DropZone
          onDrop={handleDropZoneDrop}
          allowMultiple
          accept={isOnlyImages ? SUPPORTED_IMAGE_EXTENSIONS.join(',') : SUPPORTED_FILE_EXTENSIONS.join(',')}
        >
          <DropZone.FileUpload
            actionHint={`Accepts ${getListOfItemsAsString(
              getDisplayableFileExtensionList(isOnlyImages ? SUPPORTED_IMAGE_EXTENSIONS : SUPPORTED_FILE_EXTENSIONS)
            )} files`}
          />
        </DropZone>
      </div>
      <MediaGrid items={mediaItems} size="medium" renderItem={renderItem} />
    </BlockStack>
  );
};
